import * as React from 'react';

import { EnterpriseBadge_UserState } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';

import EnterpriseLearningPathEnrollButtonNotConfigurable from 'bundles/enterprise-learning-path-description-page/components/EnterpriseLearningPathEnrollButtonNotConfigurable';
import { useGetBadgeUrl } from 'bundles/enterprise-learning-path-description-page/module-renderer/data/useGetBadgeUrl';
import HeroModule from 'bundles/enterprise-learning-path-description-page/modules/HeroModule';
import LearningObjectives from 'bundles/enterprise-learning-path-description-page/modules/LearningObjectives';
import ProductOverviewModule from 'bundles/enterprise-learning-path-description-page/modules/ProductOverviewModule';
import type { EnterpriseLearningPathPageFieldsFragment } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseLearningPathPageFragments';
import { LEARNING_PATH_NAV_ITEMS } from 'bundles/enterprise-learning-path-description-page/utils/getPageNavigationLinks';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';
import Skills from 'bundles/unified-description-page-common/components/modules/product-overview/Skills';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

type Props = {
  productTitle: string;
  pageData: EnterpriseLearningPathPageFieldsFragment;
};

const EnterpriseLearningPathProductOverviewModuleRenderer = ({ productTitle, pageData }: Props) => {
  const { navigationLinks } = usePageNavigation();
  const { programSlug, orgSlug } = useParams();
  const isOrgScopedHome = useIsOrgScopedHome();
  const myLearningUrl = isOrgScopedHome
    ? `/organizations/${orgSlug}/my-learning?myLearningTab=IN_PROGRESS`
    : `/programs/${programSlug}/my-learning?myLearningTab=IN_PROGRESS`;
  const skills = React.useMemo(
    () =>
      (pageData?.enterpriseBadge?.skillsMetadata ?? [])
        .filter((skill) => skill !== null)
        .map((skill) => (skill?.skillName ? skill.skillName : '')),
    [pageData]
  );
  const badgeUrl = useGetBadgeUrl(
    pageData.enterpriseBadge.id,
    pageData.enterpriseBadge.previewUrl ?? '',
    pageData.userState === 'ISSUED'
  );

  if (!pageData) {
    return null;
  }

  const overviewDescription = <LearningObjectives learningObjectives={pageData?.enterpriseBadge?.learningObjectives} />;
  const overviewSkills = <Skills skills={skills} />;

  return (
    <ProductOverviewModule
      heroBackgroundColor="var(--cds-color-blue-25)"
      anchorId={LEARNING_PATH_NAV_ITEMS.ABOUT}
      navigationLinks={navigationLinks}
      hero={
        <HeroModule
          productTitle={productTitle}
          productType={_t('Learning path')}
          description={pageData?.enterpriseBadge?.description}
          imageUrl={pageData?.enterpriseBadge?.imageUrl as string}
          primaryCta={
            (
              {
                [EnterpriseBadge_UserState.Enrolled]: (
                  <Button onClick={() => window.open(myLearningUrl, '_self')}>{_t('Go to learning path')}</Button>
                ),
                [EnterpriseBadge_UserState.NeverEnrolled]: <EnterpriseLearningPathEnrollButtonNotConfigurable />,
                [EnterpriseBadge_UserState.Unenrolled]: <EnterpriseLearningPathEnrollButtonNotConfigurable />,
                [EnterpriseBadge_UserState.Issued]: (
                  <Button
                    onClick={() => {
                      window.open(badgeUrl, '_self');
                    }}
                  >
                    {_t('View Badge')}
                  </Button>
                ),
              } as const
            )[pageData.userState]
          }
        />
      }
      aboutSubmodule={
        <>
          {overviewDescription}
          {overviewSkills}
        </>
      }
    />
  );
};

export default EnterpriseLearningPathProductOverviewModuleRenderer;
